import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, X, Heart, MapPin, Fuel, Gauge, Calendar, Settings, Hash, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import verifiedBadge from "../assets/verified.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'car', label: 'Cars', emoji: '' },
  { key: 'bike', label: 'Bikes', emoji: '' },
  { key: 'ev', label: 'Electric Vehicles', emoji: '' },
  { key: 'commercial', label: 'Commercial Vehicles', emoji: '' },
];

const shuffleArray = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const getVisibleCount = () => {
  if (typeof window === 'undefined') return 3;
  if (window.innerWidth < 640) return 1;
  if (window.innerWidth < 1024) return 2;
  return 3;
};

const getFuelColor = (fuel) => {
  if (!fuel) return 'text-gray-500';
  const t = fuel.toLowerCase();
  if (t.includes('petrol') || t.includes('cng')) return 'text-sky-600';
  if (t.includes('diesel')) return 'text-orange-600';
  if (t.includes('electric')) return 'text-green-600';
  if (t.includes('hybrid')) return 'text-blue-700';
  return 'text-gray-500';
};

const VerifiedBadge = () => (
  <div className="absolute top-2 left-2 z-10">
    <img
      src={verifiedBadge}
      alt="Verified"
      className="h-6 w-auto object-contain drop-shadow-sm"
      style={{ maxWidth: '88px' }}
      onError={(e) => {
        e.target.style.display = 'none';
        e.target.parentElement.innerHTML =
          '<div style="background:#2563eb;color:white;font-size:9px;font-weight:700;padding:3px 7px;border-radius:20px;display:flex;align-items:center;gap:3px;box-shadow:0 2px 8px rgba(37,99,235,0.4)">' +
          '<svg width="9" height="9" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          'VERIFIED</div>';
      }}
    />
  </div>
);

const VehicleCard = ({ vehicle, navigate, isWishlisted, onWishlist }) => (
  <div
    onClick={() => navigate(`/vehicles/${vehicle.id}`)}
    className="cursor-pointer hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden border border-gray-200 bg-white h-full"
  >
    {/* IMAGE */}
    <div className="relative h-52 overflow-hidden bg-gray-100">
      <img
        src={vehicle.images?.[0] || 'https://images.unsplash.com/photo-1676288176820-a5a954d81e6e?w=500'}
        alt={`${vehicle.brand_name} ${vehicle.model_name}`}
        className="w-full h-full object-contain transition-transform duration-300"
        onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=No+Image'; }}
      />

      {vehicle.verified_by_x7autoz && <VerifiedBadge />}

      <button
        onClick={(e) => { e.stopPropagation(); onWishlist(vehicle.id); }}
        className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-md transition ${
          isWishlisted ? 'bg-red-500' : 'bg-white hover:bg-gray-100'
        }`}
      >
        <Heart className={`h-4 w-4 ${isWishlisted ? 'text-white fill-white' : 'text-gray-600'}`} />
      </button>
    </div>

    {/* CONTENT */}
    <div className="p-3">
      <div className="flex justify-between items-start gap-2">
        <h3 className="text-base font-semibold text-gray-800 leading-tight truncate min-w-0">
          {vehicle.brand_name} {vehicle.model_name}
        </h3>
        <span className="text-amber-900 font-bold text-sm whitespace-nowrap flex-shrink-0">₹ {vehicle.price}</span>
      </div>
      <p className="text-xs text-gray-700 mt-1 truncate">{vehicle.variant_name}</p>

      {/* 6-cell grid */}
      <div className="grid grid-cols-3 mt-3">
        {/* ── Row 1 ── */}
        <div className="relative flex flex-col items-center px-2 pb-4 border-r border-gray-300">
          <Gauge className="h-4 w-4 text-black mb-1 flex-shrink-0" />
          <span className="text-[10px] text-black text-center leading-tight">{vehicle.km_run_range || '0 KM'}</span>
          <div className="absolute bottom-0 left-2 right-2 border-b border-gray-300" />
        </div>

        <div className="relative flex flex-col items-center px-2 pb-4 border-r border-gray-300">
          {vehicle.fuel_type?.toLowerCase().includes('electric')
            ? <Zap className={`h-4 w-4 mb-1 flex-shrink-0 ${getFuelColor(vehicle.fuel_type)}`} fill="currentColor" />
            : <Fuel className={`h-4 w-4 mb-1 flex-shrink-0 ${getFuelColor(vehicle.fuel_type)}`} />
          }
          <span className={`text-[10px] ${getFuelColor(vehicle.fuel_type)}`}>{vehicle.fuel_type}</span>
          <div className="absolute bottom-0 left-2 right-2 border-b border-gray-300" />
        </div>

        <div className="relative flex flex-col items-center px-2 pb-4">
          <Settings className="h-4 w-4 text-black mb-1 flex-shrink-0" />
          <span className="text-[10px] text-black">{vehicle.transmission || 'Manual'}</span>
          <div className="absolute bottom-0 left-2 right-2 border-b border-gray-300" />
        </div>

        {/* ── Row 2 ── */}
        <div className="flex flex-col items-center px-2 pt-3 border-r border-gray-300">
          <MapPin className="h-4 w-4 text-black mb-1 flex-shrink-0" />
          <span className="text-[10px] text-black text-center">{vehicle.city || 'City'}</span>
        </div>

        <div className="flex flex-col items-center px-2 pt-3 border-r border-gray-300">
          <Calendar className="h-4 w-4 text-black mb-1 flex-shrink-0" />
          <span className="text-[10px] text-black">{vehicle.manufacturing_year || '2022'}</span>
        </div>

        <div className="flex flex-col items-center px-2 pt-3">
          <Hash className="h-4 w-4 text-black mb-1 flex-shrink-0" />
          <span className="text-[9px] text-black text-center leading-tight whitespace-nowrap">
            {vehicle.registration_no || 'KA-XX'}
          </span>
        </div>
      </div>
    </div>
  </div>
);

const CategorySection = ({ label, emoji, vehicles, navigate, wishlistedIds, onWishlist }) => {
  const [shuffled, setShuffled] = useState(() => shuffleArray(vehicles));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(getVisibleCount());
  const [showAll, setShowAll] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    const onResize = () => setVisibleCount(getVisibleCount());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (vehicles.length === 0) return;
    intervalRef.current = setInterval(() => {
      setShuffled(shuffleArray(vehicles));
      setCurrentIndex(0);
    }, 10000);
    return () => clearInterval(intervalRef.current);
  }, [vehicles]);

  const maxIndex = Math.max(0, shuffled.length - visibleCount);
  const GAP = 16; // matches gap-4 = 16px

  const goNext = useCallback(() => setCurrentIndex(prev => Math.min(prev + 1, maxIndex)), [maxIndex]);
  const goPrev = useCallback(() => setCurrentIndex(prev => Math.max(prev - 1, 0)), []);

  if (vehicles.length === 0) return null;

  return (
    <div className="mb-16">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 px-1">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
            {emoji} Featured {label}
          </h2>
          <p className="text-gray-500 text-sm mt-1">{vehicles.length} vehicles available</p>
        </div>
      </div>

      {/*
        KEY FIX:
        - Outer div uses `px-10` (40px each side) so arrow buttons get their own
          dedicated gutter and never overlap the cards.
        - Arrow buttons are absolutely positioned at left-0 / right-0 within
          this padded wrapper — they sit flush inside the 40px gutters.
        - The overflow-hidden track fills exactly the space between the gutters,
          so all `visibleCount` cards render at full, equal width.
      */}
      <div className="relative px-6 sm:px-10">

        {/* ← Prev arrow */}
        <button
          onClick={goPrev}
          disabled={currentIndex === 0}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-7 h-7 sm:w-9 sm:h-9 bg-white border border-gray-200 rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
          aria-label="Previous"
        >
          <ChevronLeft className="h-5 w-5 text-gray-700" />
        </button>

        {/* Sliding track */}
        <div className="overflow-hidden w-full">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{
              gap: `${GAP}px`,
              // Each card width = (100% / visibleCount) minus its share of gaps
              // Translate by exactly one card-width + one gap per step
              transform: `translateX(calc(-${currentIndex} * (100% / ${visibleCount} + ${GAP / visibleCount}px)))`,
            }}
          >
            {shuffled.map((vehicle, idx) => (
              <div
                key={`${vehicle.id}-${idx}`}
                style={{
                  flex: `0 0 calc(${100 / visibleCount}% - ${(GAP * (visibleCount - 1)) / visibleCount}px)`,
                  minWidth: 0,
                }}
              >
                <VehicleCard
                  vehicle={vehicle}
                  navigate={navigate}
                  isWishlisted={wishlistedIds.has(vehicle.id)}
                  onWishlist={onWishlist}
                />
              </div>
            ))}
          </div>
        </div>

        {/* → Next arrow */}
        <button
          onClick={goNext}
          disabled={currentIndex >= maxIndex}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-7 h-7 sm:w-9 sm:h-9 bg-white border border-gray-200 rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
          aria-label="Next"
        >
          <ChevronRight className="h-5 w-5 text-gray-700" />
        </button>
      </div>

      {/* Dot indicators */}
      {shuffled.length > visibleCount && (
        <div className="flex justify-center gap-1.5 mt-4">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === currentIndex ? 'w-6 bg-orange-500' : 'w-1.5 bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}

      {/* View All Button */}
      <div className="flex justify-center mt-6">
        <button
          onClick={() => setShowAll(true)}
          className="px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full text-sm transition shadow-md hover:shadow-lg"
        >
          View All {label} ({vehicles.length})
        </button>
      </div>

      {/* View All Modal */}
      {showAll && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-xl font-bold text-gray-800">{emoji} All Featured {label}</h3>
              <button
                onClick={() => setShowAll(false)}
                className="w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicles.map((vehicle) => (
                <VehicleCard
                  key={vehicle.id}
                  vehicle={vehicle}
                  navigate={navigate}
                  isWishlisted={wishlistedIds.has(vehicle.id)}
                  onWishlist={onWishlist}
                />
              ))}
            </div>
            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-center rounded-b-2xl">
              <button
                onClick={() => setShowAll(false)}
                className="px-8 py-2.5 bg-gray-800 text-white rounded-full font-medium hover:bg-gray-900 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FeaturedVehiclesSection = ({ selectedCity }) => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [categoryData, setCategoryData] = useState({ car: [], bike: [], ev: [], commercial: [] });
  const [loading, setLoading] = useState(true);
  const [wishlistedIds, setWishlistedIds] = useState(new Set());

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const url = selectedCity
          ? `${API}/featured-vehicles/by-category?city=${selectedCity}`
          : `${API}/featured-vehicles/by-category`;
        const res = await axios.get(url);
        setCategoryData(res.data);
      } catch (err) {
        console.error('Failed to load featured vehicles:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCity]);

  useEffect(() => {
    if (!user || !token) return;
    const fetchWishlist = async () => {
      try {
        const res = await axios.get(`${API}/wishlist`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWishlistedIds(new Set(res.data.vehicles.map((v) => v.id)));
      } catch {}
    };
    fetchWishlist();
  }, [user, token]);

  const handleWishlist = async (vehicleId) => {
    if (!user) { navigate('/login'); return; }
    try {
      if (wishlistedIds.has(vehicleId)) {
        await axios.delete(`${API}/wishlist/${vehicleId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWishlistedIds((prev) => { const s = new Set(prev); s.delete(vehicleId); return s; });
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist?vehicle_id=${vehicleId}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWishlistedIds((prev) => new Set([...prev, vehicleId]));
        toast.success('Added to wishlist!');
      }
    } catch {
      toast.error('Failed to update wishlist');
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto" />
        <p className="text-gray-400 mt-3 text-sm">Loading featured vehicles...</p>
      </div>
    );
  }

  const hasAny = CATEGORIES.some((c) => categoryData[c.key]?.length > 0);
  if (!hasAny) return null;

  return (
    <section className="py-12 px-4 md:px-8 lg:px-12 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {CATEGORIES.map((cat) => (
          <CategorySection
            key={cat.key}
            categoryKey={cat.key}
            label={cat.label}
            emoji={cat.emoji}
            vehicles={categoryData[cat.key] || []}
            navigate={navigate}
            wishlistedIds={wishlistedIds}
            onWishlist={handleWishlist}
          />
        ))}
      </div>
    </section>
  );
};

export default FeaturedVehiclesSection;